import { BIDDING_TYPES } from "../../utils";
import {
  BuyerSellerResponse,
  DELIVERY_TYPE,
  MaterialType,
  OrderToSubmitRequest,
} from "../../types";

/**
 * Pure `ordersToSubmit` transforms lifted verbatim from the pre-Carbon
 * Order.tsx handler bodies. Each takes the current orders array and returns the
 * next one; the hook (useOrderBuilder) wires them to setState. Kept pure so the
 * intricate supplier-selection / invite logic that feeds the create payload can
 * be reasoned about (and unit-tested) without a rendered component.
 */

/** step 2: add a material line, or remove it if already selected. */
export const toggleMaterial = (
  orders: OrderToSubmitRequest[],
  material: any
): OrderToSubmitRequest[] => {
  const indexOfMaterial = orders.findIndex(
    (mat: any) => material.id === mat.id
  );
  if (indexOfMaterial === -1) {
    return [
      ...orders,
      {
        id: material.id,
        ean: material.ean,
        measurementUnit: material.measurementUnit,
        globalMaterialName: material.globalMaterialName,
        deliveryPlace: material.deliveryPlace,
        fileUrl: material.fileURL,
        type: material.category.type,
        spot: DELIVERY_TYPE.SPOT,
        suppliers: [],
        company: material.company,
      } as unknown as OrderToSubmitRequest,
    ];
  }
  return [
    ...orders.slice(0, indexOfMaterial),
    ...orders.slice(indexOfMaterial + 1),
  ];
};

/** the material line seeded from a `?id=` deep link. */
export const seedMaterialLine = (material: any): OrderToSubmitRequest =>
  ({
    id: material.id,
    ean: material.ean,
    measurementUnit: material.measurementUnit,
    globalMaterialName: material.globalMaterialName,
    deliveryPlace: material.deliveryPlace,
    fileUrl: material.fileURL,
    type: material.category.type as unknown as MaterialType,
    spot: DELIVERY_TYPE.SPOT,
    suppliers: [],
    company: material.company,
  }) as unknown as OrderToSubmitRequest;

/** step 3: add/remove one existing supplier for one material. */
export const toggleSupplier = (
  orders: OrderToSubmitRequest[],
  supplier: BuyerSellerResponse,
  materialId: string
): OrderToSubmitRequest[] => {
  const ordIndex = orders.findIndex((order: any) => order.id === materialId);
  if (ordIndex === -1) return orders;

  const foundOrder = orders[ordIndex];
  const existingSupplierIndex = foundOrder?.suppliers.findIndex(
    (sup: any) => sup.id === supplier.seller.id
  );

  return orders.map((item: any, index: number) => {
    if (index === ordIndex) {
      if (existingSupplierIndex === -1) {
        return {
          ...item,
          suppliers: [
            ...item.suppliers,
            {
              id: supplier.seller.id,
              validated: supplier.validated,
              email: supplier.seller.email,
              name: `${supplier.seller.firstName || ""} ${supplier.seller.lastName || ""}`.trim(),
              companyName: supplier.seller.company?.name || "",
            },
          ],
        };
      }
      return {
        ...item,
        suppliers: item.suppliers.filter(
          (sup: any) => sup.id !== supplier.seller.id
        ),
      };
    }
    return item;
  });
};

/** step 3 "select all" (simple/multiple): per-material available suppliers. */
export const selectAllSuppliers = (
  orders: OrderToSubmitRequest[],
  suppliersData: any[],
  isAllSelected: boolean
): OrderToSubmitRequest[] => {
  if (isAllSelected) {
    return orders.map((item: any) => ({
      ...item,
      suppliers: item.suppliers.filter((sup: any) => sup.emailsList),
    }));
  }
  return orders.map((item: any, index: number) => {
    const materialSuppliers = suppliersData[index] || [];
    const availableSuppliersForMaterial = materialSuppliers
      .filter((sup: any) => sup.seller?.id)
      .map((sup: any) => ({
        id: sup.seller.id,
        validated: sup.validated,
        email: sup.seller.email,
        name: `${sup.seller.firstName || ""} ${sup.seller.lastName || ""}`.trim(),
        companyName: sup.seller.company?.name || "",
      }));

    const emailInvitations = item.suppliers.filter((sup: any) => sup.emailsList);
    const existingSupplierIds = item.suppliers
      .filter((sup: any) => sup.id)
      .map((sup: any) => sup.id);
    const newAvailableSuppliers = availableSuppliersForMaterial.filter(
      (sup: any) => !existingSupplierIds.includes(sup.id)
    );

    return {
      ...item,
      suppliers: [
        ...emailInvitations,
        ...item.suppliers.filter((sup: any) => sup.id),
        ...newAvailableSuppliers,
      ],
    };
  });
};

/** step 3 "select all" (joint): every unique available supplier on every line. */
export const selectAllJointSuppliers = (
  orders: OrderToSubmitRequest[],
  suppliersData: Record<string, any[]>,
  isAllSelected: boolean
): OrderToSubmitRequest[] => {
  const suppliersObject = Object.keys(suppliersData).map(
    (key) => suppliersData[key]
  );

  const uniqueSuppliers = new Map();
  suppliersObject.forEach((group: any) => {
    group.forEach((entry: any) => {
      const supplier = entry.seller;
      if (supplier && supplier.id) {
        uniqueSuppliers.set(supplier.id, {
          id: supplier.id,
          validated: entry.validated,
          email: supplier.email,
          name: `${supplier.firstName || ""} ${supplier.lastName || ""}`.trim(),
          companyName: supplier.company?.name || "",
        });
      }
    });
  });
  const availableSuppliers = Array.from(uniqueSuppliers.values());

  if (isAllSelected) {
    return orders.map((item: any) => ({
      ...item,
      suppliers: item.suppliers.filter((sup: any) => sup.emailsList),
    }));
  }
  return orders.map((item: any) => {
    const emailInvitations = item.suppliers.filter((sup: any) => sup.emailsList);
    const existingSupplierIds = item.suppliers
      .filter((sup: any) => sup.id)
      .map((sup: any) => sup.id);
    const newAvailableSuppliers = availableSuppliers.filter(
      (sup: any) => !existingSupplierIds.includes(sup.id)
    );
    return {
      ...item,
      suppliers: [
        ...emailInvitations,
        ...item.suppliers.filter((sup: any) => sup.id),
        ...newAvailableSuppliers,
      ],
    };
  });
};

type InviteArg =
  | { emailsList: string; materialId: string }
  | {
      materialId: string;
      supplierId: string;
      supplierInfo: {
        id: string;
        email: string;
        name: string;
        companyName: string;
        validated: boolean;
      };
    };

/**
 * step 3 invite dispatcher (existing/potential supplier add-remove, per-material
 * clear, joint invite, bulk email invites, single email toggle). Lifted verbatim
 * from Order.tsx#handleOrdersToSubmit — `orders` replaces the `ordersToSubmit`
 * closure and `selectedComplexType` is passed in.
 */
export const applyInvite = (
  orders: OrderToSubmitRequest[],
  invite: InviteArg,
  type: any,
  selectedComplexType: any
): OrderToSubmitRequest[] => {
  if (type === "clearAllForMaterial") {
    const materialInvite = invite as any;
    const ordIndex = orders.findIndex(
      (order: any) => order.id === materialInvite.materialId
    );
    if (ordIndex !== -1) {
      return orders.map((item: any, index: number) =>
        index === ordIndex ? { ...item, suppliers: [] } : item
      );
    }
    return orders;
  }

  if (type === "existingSupplier") {
    const supplierInvite = invite as any;
    if (
      supplierInvite.materialId === "ALL" &&
      selectedComplexType?.value === BIDDING_TYPES.JOINT
    ) {
      return orders.map((item: any) => {
        const supplierExists = item.suppliers.some(
          (sup: any) => sup.id === supplierInvite.supplierId
        );
        return {
          ...item,
          suppliers: supplierExists
            ? item.suppliers
            : [
                ...item.suppliers,
                {
                  id: supplierInvite.supplierId,
                  email: supplierInvite.supplierInfo.email,
                  name: supplierInvite.supplierInfo.name,
                  companyName: supplierInvite.supplierInfo.companyName,
                  validated: supplierInvite.supplierInfo.validated,
                },
              ],
        };
      });
    }
    const ordIndex = orders.findIndex(
      (order: any) => order.id === supplierInvite.materialId
    );
    const targetIndex =
      ordIndex !== -1 ? ordIndex : orders.length === 1 ? 0 : -1;
    if (targetIndex !== -1) {
      return orders.map((item: any, index: number) => {
        if (index === targetIndex) {
          const supplierExists = item.suppliers.some(
            (sup: any) => sup.id === supplierInvite.supplierId
          );
          return {
            ...item,
            suppliers: supplierExists
              ? item.suppliers
              : [
                  ...item.suppliers,
                  {
                    id: supplierInvite.supplierId,
                    email: supplierInvite.supplierInfo.email,
                    name: supplierInvite.supplierInfo.name,
                    companyName: supplierInvite.supplierInfo.companyName,
                    validated: supplierInvite.supplierInfo.validated,
                  },
                ],
          };
        }
        return item;
      });
    }
    return orders;
  }

  if (type === "removeExistingSupplier") {
    const supplierInvite = invite as any;
    if (
      supplierInvite.materialId === "ALL" &&
      selectedComplexType?.value === BIDDING_TYPES.JOINT
    ) {
      return orders.map((item: any) => ({
        ...item,
        suppliers: item.suppliers.filter(
          (sup: any) => sup.id !== supplierInvite.supplierId
        ),
      }));
    }
    const ordIndex = orders.findIndex(
      (order: any) => order.id === supplierInvite.materialId
    );
    const targetIndex =
      ordIndex !== -1 ? ordIndex : orders.length === 1 ? 0 : -1;
    if (targetIndex !== -1) {
      return orders.map((item: any, index: number) =>
        index === targetIndex
          ? {
              ...item,
              suppliers: item.suppliers.filter(
                (sup: any) => sup.id !== supplierInvite.supplierId
              ),
            }
          : item
      );
    }
    return orders;
  }

  if (type === "isJoint") {
    const emailInvite = invite as any;
    return orders.map((item: any) => {
      const updatedSuppliers = item.suppliers.filter(
        (supplier: any) => supplier.emailsList !== emailInvite.emailsList
      );
      const emailExists = updatedSuppliers.length !== item.suppliers.length;
      return {
        ...item,
        suppliers: emailExists
          ? updatedSuppliers
          : [
              ...item.suppliers,
              { emailsList: emailInvite.emailsList, materialId: item.id },
            ],
      };
    });
  }

  if (type === "setEmailInvites") {
    const setInvite = invite as unknown as {
      materialId: string;
      emails: string[];
    };
    const targetAll = setInvite.materialId === "ALL";
    return orders.map((item: any) => {
      if (!targetAll && item.id !== setInvite.materialId) return item;
      const nonEmailSuppliers = item.suppliers.filter(
        (sup: any) => !sup.emailsList
      );
      const newEmailEntries = setInvite.emails.map((email) => ({
        materialId: item.id,
        emailsList: email,
      }));
      return { ...item, suppliers: [...nonEmailSuppliers, ...newEmailEntries] };
    });
  }

  const emailInvite = invite as any;
  const ordIndex = orders.findIndex(
    (order: any) => order.id === emailInvite.materialId
  );
  const targetIndex = ordIndex !== -1 ? ordIndex : orders.length === 1 ? 0 : -1;
  if (targetIndex !== -1) {
    return orders.map((item: any, index: number) => {
      if (index === targetIndex) {
        const updatedSuppliers = item.suppliers.filter(
          (supplier: any) => supplier.emailsList !== emailInvite.emailsList
        );
        const emailExists = updatedSuppliers.length !== item.suppliers.length;
        return {
          ...item,
          suppliers: emailExists
            ? updatedSuppliers
            : [...item.suppliers, emailInvite],
        };
      }
      return item;
    });
  }
  return orders;
};
